<style scoped>
.modal-footer {
    display: block;
    text-align: center;
}
.pdfPage {
    border: 5px solid #cccccc;
    max-height: 50vh;
    overflow: scroll;
}
.secondVerificationSection,
.firstVerificationSection,
.verificationOr {
    border-bottom: 1px solid #999;
    font-size: large;
}
.modal-content {
    width: min(90vw, 1800px);
}
</style>
<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">Confirm Submission</h2>
            <button type="button" class="close icon icon-close" aria-label="Close" aria-hidden="true" @click="$emit('close')" autofocus :disabled="submitting" />
        </div>
        <div class="modal-body" describedby-text>
            <div v-show="!submitting">
                <p>To finish this submission, you must sign.</p>
                <div class="pdfPage">
                    <pdf v-for="i in numPages" :key="i" :src="pdfDataTask" :page="i" />
                </div>
                <p>By submitting this signature, you agree that:</p>
                <!-- TODO - actual legal term -->
                <ol>
                    <li>you accept doing business electronically</li>
                    <li>you intend to sign</li>
                </ol>
                <div class="alert alert-dark">To receive a non-electronic version of this document, please contact us.</div>
                <!-- style="width: 50%; display: inline-block" -->
                <div v-if="messages.length > 0" class="alert alert-danger">
                    <span>Issues:</span>
                    <ul>
                        <li v-for="message in messages">{{ message }}</li>
                    </ul>
                </div>
                <div class="row" v-if="requireUserVerification">
                    <div class="col-12 text-center mb-2 firstVerificationSection">
                        <strong>First</strong>
                    </div>
                    <div class="form-group col-md-8">
                        <label class="control-label">Verify Email</label>
                        <input type="email" placeholder="example@example.com" class="form-control" id="sendVerifyEmailControl" v-model="verifyEmail" />
                    </div>
                    <div class="form-group col-md-4">
                        <br />
                        <button class="btn btn-primary" id="sendVerifyEmailBtn" @click="sendVerify('Email')" :disabled="submitting">Send Verify Code by Email</button>
                        <AsyncOperationStatus :promise="sendVerifyPromiseEmail" />
                    </div>
                    <template v-if="enableSmsMessages">
                        <div class="col-12 text-center mb-2 verificationOr">
                            <strong>OR</strong>
                        </div>
                        <div class="form-group col-md-8">
                            <label class="control-label">Verify Phone Number</label>
                            <input type="phone" placeholder="(999) 999-9999" class="form-control" ref="verifyPhoneNumber" @input="handlePhoneInput" id="sendVerifyPhoneControl" :value="verifyPhone" />
                        </div>
                        <div class="form-group col-md-4">
                            <br />
                            <button class="btn btn-primary" id="sendVerifyPhoneBtn" @click="sendVerify('Phone')" :disabled="submitting">Send Verify Code by SMS</button>
                            <AsyncOperationStatus :promise="sendVerifyPromisePhone" />
                        </div>
                    </template>
                </div>
                <div class="row">
                    <div class="col-12 text-center mb-2 secondVerificationSection" v-if="requireUserVerification">
                        <strong>Second</strong>
                    </div>
                    <div class="form-group" :class="[requireUserVerification ? 'col-md-4' : 'col-md-6']">
                        <label class="control-label">Sign Name</label>
                        <input type="text" placeholder="Signer's Full Name" class="form-control" id="signNameControl" v-model="signName" />
                    </div>
                    <div class="form-group" :class="[requireUserVerification ? 'col-md-4' : 'col-md-6']">
                        <label class="control-label">Sign Date</label>
                        <input type="text" :readonly="!manuallyEnterSignDate" :placeholder="signDatePlaceholder" :class="manuallyEnterSignDate ? ['form-control'] : ['form-control-plaintext']" id="signDateControl" v-model="signDate" />
                    </div>
                    <div class="form-group col-md-4" v-if="requireUserVerification">
                        <label class="control-label">Verify Code</label>
                        <input type="text" inputmode="numeric" placeholder="999999" class="form-control" id="verifyCodeControl" v-model="verifyCode" />
                    </div>
                </div>
            </div>
            <div v-if="submitting">
                <LoadingWheel :width="100" />
            </div>
        </div>
        <div class="modal-footer">
            <button class="btn btn-primary" id="signSubmitBtn" @click="signAndSubmit" :disabled="submitting">Sign and Submit</button>
            <button class="btn btn-outline-secondary" @click="$emit('close')" :disabled="submitting">Cancel</button>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, defineAsyncComponent, ref, onMounted, computed, SetupContext } from "vue";
import { createTextMaskInputElement } from "text-mask-core";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { useMainStore } from "@/mainStore";
import { asyncComputed } from "@vueuse/core";
import { dialogSetup } from "@/lib/dialogMixin.3";
import type pdfJs from "pdfjs-dist";
interface Props {
    requireUserVerification: boolean;
    pdfData: typeof pdfJs;
    sendVerifyFunction: (verifyEmail: string, verifyPhoneNumber: string) => { success: boolean; messages?: string[] };
    signAndSubmitFunction: (signName: string, signDate: string, verifyCode: string) => { success: boolean; messages?: string[] };
}
export default defineComponent({
    components: {
        LoadingWheel,
        AsyncOperationStatus,
        pdf: defineAsyncComponent({
            loadingComponent: LoadingWheel,
            loader: () => import(/* webpackChunkName: "PdfRenderer" */ "@/lib/vue-pdf/src/vuePdfNoSss.3.vue"),
        }),
    },
    props: ["requireUserVerification", "pdfData", "sendVerifyFunction", "signAndSubmitFunction"],
    setup(props: Props, { emit }: SetupContext) {
        const mainStore = useMainStore();
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef);

        const verifyPhoneNumberRef = ref<HTMLElement>(null!);
        const manuallyEnterSignDate = ref(false);
        const submitting = ref(false);
        const signName = ref<string>(null!);
        const signDate = ref<string>(null!);
        const verifyEmail = ref<string>(null!);
        const verifyPhone = ref<string>(null!);
        const sendVerifyPromiseEmail = ref<Promise<void>>(null!);
        const sendVerifyPromisePhone = ref<Promise<void>>(null!);
        const verifyCode = ref<string>(null!);
        const pdfDataTask = ref<ReturnType<(typeof pdfJs)["getDocument"]>>(null!);
        const numPages = ref<number>(null!);
        const messages = ref<string[]>([]);
        const phoneMask = ref<ReturnType<typeof createTextMaskInputElement>>(null!);

        const enableSmsMessages = asyncComputed(() => {
            return mainStore.isFeatureFlagActiveAsync("SmsMessages");
        });
        const signDatePlaceholder = computed(() => {
            const now = new Date();
            return `${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}-${now.getFullYear()}`;
        });

        const handlePhoneInput = (event: Event) => {
            if (phoneMask.value) {
                phoneMask.value.update((event.target! as HTMLInputElement).value);
            }
            verifyPhone.value = (event.target! as HTMLInputElement).value;
        };
        const sendVerify = (type: string) => {
            const isEmail = type == "Email";
            const promise = (async () => {
                const result = await (isEmail ? props.sendVerifyFunction(verifyEmail.value, null!) : props.sendVerifyFunction(null!, verifyPhone.value));
                if (result.success) {
                    messages.value = [];
                } else {
                    messages.value = result.messages!;
                    throw new Error("Failed");
                }
            })();
            if (isEmail) {
                sendVerifyPromiseEmail.value = promise;
            } else {
                sendVerifyPromisePhone.value = promise;
            }
        };
        const signAndSubmit = async () => {
            submitting.value = true;
            const result = await props.signAndSubmitFunction(signName.value, signDate.value, verifyCode.value);
            submitting.value = false;
            if (result.success) {
                emit("close");
            } else {
                messages.value = result.messages!;
            }
        };

        onMounted(() => {
            if (!manuallyEnterSignDate.value) {
                signDate.value = signDatePlaceholder.value;
            }
            setTimeout(async () => {
                // to avoid pdf threading issues on double sign
                const pdf = (await import(/* webpackChunkName: "PdfRenderer" */ "@/lib/vue-pdf/src/vuePdfNoSss.3.vue")).default;
                pdfDataTask.value = pdf.createLoadingTask(props.pdfData);
                pdfDataTask.value.promise.then((pdf: { numPages: number }) => {
                    numPages.value = pdf.numPages;
                });
            }, 0);
            if (props.requireUserVerification) {
                const createTextMaskFunc = () => {
                    if (verifyPhoneNumberRef.value == null) {
                        setTimeout(createTextMaskFunc, 100);
                    } else {
                        const config = {
                            inputElement: verifyPhoneNumberRef.value,
                            mask: ["(", /\d/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
                            guide: false,
                            placeholderChar: "\u2000",
                        };
                        phoneMask.value = createTextMaskInputElement(config);
                    }
                };
                createTextMaskFunc();
            }
        });

        return {
            verifyPhoneNumber: verifyPhoneNumberRef,
            container: containerRef,
            manuallyEnterSignDate,
            submitting,
            signName,
            signDate,
            verifyEmail,
            verifyPhone,
            sendVerifyPromiseEmail,
            sendVerifyPromisePhone,
            verifyCode,
            pdfDataTask,
            numPages,
            messages,

            enableSmsMessages,
            signDatePlaceholder,

            handlePhoneInput,
            sendVerify,
            signAndSubmit,
        };
    },
});
</script>
